import React, { useState, useEffect, useCallback } from 'react';
import apiKeyService from '../services/apiKeyService';
import UsageChart from './UsageChart';
import { Card, Button, ConfirmModal } from '../components/common';

/**
 * API Key Manager - AI API 키 관리
 */
const ApiKeyManager = () => {
  const [keys, setKeys] = useState([]);
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState(null);
  const [balanceInfo, setBalanceInfo] = useState({});
  const [checkingBalanceId, setCheckingBalanceId] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ open: false, key: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 새 키 추가 폼 상태
  const [newKey, setNewKey] = useState({
    provider: '',
    label: '',
    apiKey: '',
    model: ''
  });

  // 데이터 로드
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [keysRes, statsRes, providersRes] = await Promise.all([
        apiKeyService.getApiKeys(),
        apiKeyService.getApiKeyStats(),
        apiKeyService.getProviders()
      ]);

      if (keysRes.success) {
        setKeys(keysRes.keys);
      }
      if (statsRes.success) {
        setStats(statsRes.stats);
      }
      if (providersRes.success) {
        const providersObj = {};
        providersRes.providers.forEach(p => {
          providersObj[p.id] = p;
        });
        setProviders(providersObj);
      }
      setError(null);
    } catch (err) {
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 메시지 자동 숨김
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // 키 추가
  const handleAddKey = async (e) => {
    e.preventDefault();
    try {
      const result = await apiKeyService.addApiKey(
        newKey.provider,
        newKey.label,
        newKey.apiKey,
        newKey.model || providers[newKey.provider]?.defaultModel
      );

      if (result.success) {
        setShowAddForm(false);
        setNewKey({ provider: '', label: '', apiKey: '', model: '' });
        setMessage({ type: 'success', text: 'API 키가 추가되었습니다.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '키 추가에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '키 추가 중 오류가 발생했습니다.' });
    }
  };

  // 프로바이더 변경 시 기본 모델 설정
  const handleProviderChange = (provider) => {
    setNewKey({
      ...newKey,
      provider,
      model: providers[provider]?.defaultModel || ''
    });
  };

  // 이름 편집 시작
  const handleLabelEditStart = (keyId, currentLabel) => {
    setEditingLabelId(keyId);
    setEditingLabelValue(currentLabel);
  };

  // 이름 편집 저장
  const handleLabelEditSave = async (keyId) => {
    if (!editingLabelValue.trim()) {
      setMessage({ type: 'error', text: '이름을 입력해주세요.' });
      return;
    }
    try {
      const result = await apiKeyService.updateApiKey(keyId, { label: editingLabelValue.trim() });
      if (result.success) {
        setMessage({ type: 'success', text: '이름이 변경되었습니다.' });
        setEditingLabelId(null);
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '이름 변경에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '이름 변경 중 오류가 발생했습니다.' });
    }
  };

  // 이름 편집 취소
  const handleLabelEditCancel = () => {
    setEditingLabelId(null);
    setEditingLabelValue('');
  };

  // 모델 변경
  const handleModelChange = async (keyId, newModel) => {
    try {
      const result = await apiKeyService.updateApiKey(keyId, { model: newModel });
      if (result.success) {
        setMessage({ type: 'success', text: '모델이 변경되었습니다.' });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '모델 변경에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '모델 변경 중 오류가 발생했습니다.' });
    }
  };

  // 키 활성화/비활성화
  const handleToggleActive = async (id, currentActive) => {
    try {
      const result = await apiKeyService.updateApiKey(id, { isActive: !currentActive });
      if (result.success) {
        loadData();
      }
    } catch (err) {
      setMessage({ type: 'error', text: '상태 변경에 실패했습니다.' });
    }
  };

  // 키 삭제 모달 열기
  const handleDeleteClick = (key) => {
    setDeleteModal({ open: true, key });
  };

  // 키 삭제 확인
  const handleDeleteConfirm = async () => {
    if (!deleteModal.key) return;

    setDeleteLoading(true);
    try {
      const result = await apiKeyService.deleteApiKey(deleteModal.key.id);
      if (result.success) {
        setMessage({ type: 'success', text: '키가 삭제되었습니다.' });
        setDeleteModal({ open: false, key: null });
        loadData();
      } else {
        setMessage({ type: 'error', text: result.error || '삭제에 실패했습니다.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: '삭제 중 오류가 발생했습니다.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  // 키 테스트
  const handleTest = async (id) => {
    setTestingId(id);
    setTestResult(null);

    try {
      const result = await apiKeyService.testApiKey(id);
      if (result.success) {
        setTestResult({ id, ...result.test });
      }
    } catch (err) {
      setTestResult({ id, success: false, message: '테스트 중 오류 발생' });
    } finally {
      setTestingId(null);
    }
  };

  // 잔액 확인
  const handleCheckBalance = async (id) => {
    setCheckingBalanceId(id);
    try {
      const result = await apiKeyService.getApiKeyBalance(id);
      if (result.success) {
        setBalanceInfo(prev => ({ ...prev, [id]: result.balance }));
      }
    } catch (err) {
      setBalanceInfo(prev => ({ ...prev, [id]: { supported: false, message: '조회 실패' } }));
    } finally {
      setCheckingBalanceId(null);
    }
  };

  // 프로바이더별 키 통계
  const getProviderStats = () => {
    const stats = {};
    Object.keys(providers).forEach(key => {
      const providerKeys = keys.filter(k => k.provider === key);
      stats[key] = {
        total: providerKeys.length,
        active: providerKeys.filter(k => k.isActive).length
      };
    });
    return stats;
  };

  const providerStats = getProviderStats();

  // 필터링된 키 목록
  const filteredKeys = keys.filter(k => {
    if (providerFilter === 'all') return true;
    return k.provider === providerFilter;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="text-gray-600 text-sm">API 키 정보를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1400px] mx-auto max-md:gap-4">
      {/* 메시지 */}
      {message && (
        <div className={`py-3 px-4 rounded-lg text-sm font-medium ${
          message.type === 'success' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
        }`}>
          {message.text}
        </div>
      )}

      {error && (
        <div className="py-3 px-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* 헤더 */}
      <div className="flex justify-between items-start gap-4 max-md:flex-col max-md:gap-4">
        <div>
          <h1 className="text-[28px] font-bold text-gray-900 m-0 mb-1 max-md:text-2xl">AI API 키 관리</h1>
          <p className="text-sm text-gray-600 m-0">AI 서비스 연동에 필요한 API 키를 관리합니다</p>
        </div>
        <div>
          <Button
            variant="primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? '취소' : '+ 새 키 추가'}
          </Button>
        </div>
      </div>

      {/* 사용량 그래프 */}
      <UsageChart />

      {/* 프로바이더 상태 요약 */}
      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1 max-md:gap-3">
        {Object.entries(providers).map(([key, info]) => {
          const stat = providerStats[key] || { total: 0, active: 0 };
          const hasKey = stat.total > 0;

          return (
            <div
              key={key}
              className={`flex flex-col gap-3 p-5 border rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md max-md:p-4 ${
                hasKey ? 'border-green-500 bg-gradient-to-b from-green-50 to-white' : 'border-gray-200 bg-gray-50'
              } ${
                providerFilter === key ? 'bg-blue-500 border-blue-600' : ''
              }`}
              onClick={() => setProviderFilter(providerFilter === key ? 'all' : key)}
            >
              <div className="flex justify-between items-center">
                <span className={`text-[15px] font-semibold ${providerFilter === key ? 'text-white' : 'text-gray-900'}`}>
                  {info.name}
                </span>
                <span className={`py-1 px-2 rounded-md text-[11px] font-semibold uppercase ${
                  providerFilter === key
                    ? 'bg-white/20 text-white'
                    : info.tier === 'free'
                      ? 'bg-green-100 text-green-600'
                      : 'bg-amber-100 text-amber-600'
                }`}>
                  {info.tier === 'free' ? '무료' : '유료'}
                </span>
              </div>
              <div className="text-center py-3">
                {hasKey ? (
                  <>
                    <span className={`text-[28px] font-bold leading-none ${providerFilter === key ? 'text-white' : 'text-green-500'}`}>
                      {stat.active}/{stat.total}
                    </span>
                    <span className={`block text-xs mt-1 ${providerFilter === key ? 'text-white' : 'text-gray-600'}`}>
                      활성 키
                    </span>
                  </>
                ) : (
                  <span className={`text-sm ${providerFilter === key ? 'text-white' : 'text-gray-400'}`}>
                    키 없음
                  </span>
                )}
              </div>
              <a
                href={info.registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`block text-center py-2 px-3 rounded-md text-xs font-medium no-underline transition-all ${
                  providerFilter === key
                    ? 'bg-white/15 border border-white/30 text-white'
                    : 'bg-slate-100 border border-gray-200 text-blue-500 hover:bg-blue-50 hover:border-blue-500'
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {hasKey ? '키 발급 페이지' : '키 발급받기'} →
              </a>
            </div>
          );
        })}
      </div>

      {/* 새 키 추가 폼 */}
      {showAddForm && (
        <Card className="bg-white">
          <form onSubmit={handleAddKey}>
            <h3 className="text-lg font-semibold text-gray-900 m-0 mb-5">새 API 키 추가</h3>
            <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
              <div className="mb-4">
                <label className="block text-[13px] font-medium text-gray-900 mb-1.5">프로바이더</label>
                <select
                  value={newKey.provider}
                  onChange={e => handleProviderChange(e.target.value)}
                  required
                  className="w-full py-2.5 px-3.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white transition-all focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                >
                  <option value="">선택하세요</option>
                  {Object.entries(providers).map(([key, info]) => (
                    <option key={key} value={key}>
                      {info.name} ({info.tier === 'free' ? '무료' : '유료'})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-[13px] font-medium text-gray-900 mb-1.5">라벨 (구분용)</label>
                <input
                  type="text"
                  value={newKey.label}
                  onChange={e => setNewKey({ ...newKey, label: e.target.value })}
                  placeholder="예: 메인 키, 백업 키"
                  required
                  className="w-full py-2.5 px-3.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white transition-all focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10 placeholder:text-gray-400"
                />
              </div>
            </div>
            {newKey.provider && providers[newKey.provider]?.models && (
              <div className="mb-4">
                <label className="block text-[13px] font-medium text-gray-900 mb-1.5">사용 모델</label>
                <select
                  value={newKey.model}
                  onChange={e => setNewKey({ ...newKey, model: e.target.value })}
                  required
                  className="w-full py-2.5 px-3.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white transition-all focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10"
                >
                  {providers[newKey.provider].models.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.name} - {m.description}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-gray-900 mb-1.5">API 키</label>
              <input
                type="password"
                value={newKey.apiKey}
                onChange={e => setNewKey({ ...newKey, apiKey: e.target.value })}
                placeholder="API 키 입력..."
                required
                className="w-full py-2.5 px-3.5 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white transition-all focus:outline-none focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/10 placeholder:text-gray-400"
              />
            </div>
            {newKey.provider && providers[newKey.provider] && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <p className="text-sm text-blue-800 m-0 mb-2 leading-relaxed">{providers[newKey.provider].description}</p>
                <a
                  href={providers[newKey.provider].registrationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-500 no-underline hover:underline"
                >
                  {providers[newKey.provider].name} 콘솔에서 키 발급받기 →
                </a>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" variant="primary">추가하기</Button>
              <Button type="button" variant="secondary" onClick={() => setShowAddForm(false)}>취소</Button>
            </div>
          </form>
        </Card>
      )}

      {/* 필터 표시 */}
      {providerFilter !== 'all' && (
        <div className="inline-flex items-center gap-2 py-2 px-3 bg-blue-50 border border-blue-200 rounded-full text-[13px] text-blue-800">
          <span>{providers[providerFilter]?.name} 필터링 중</span>
          <button
            onClick={() => setProviderFilter('all')}
            className="bg-transparent border-none text-sm text-blue-500 cursor-pointer p-0 leading-none hover:text-blue-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* 등록된 키 목록 */}
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-gray-900 m-0">등록된 API 키 ({filteredKeys.length})</h3>
        {filteredKeys.length === 0 ? (
          <Card className="bg-white">
            <div className="flex flex-col items-center py-12 px-6 gap-2">
              <span className="text-5xl leading-none">🔑</span>
              <p className="text-[15px] font-semibold text-gray-900 m-0">등록된 API 키가 없습니다</p>
              <p className="text-sm text-gray-600 m-0">위의 "새 키 추가" 버튼을 눌러 키를 등록하세요.</p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4 max-md:grid-cols-1">
            {filteredKeys.map(key => (
              <Card key={key.id} className={`bg-white transition-all hover:-translate-y-0.5 hover:shadow-md ${!key.isActive ? 'opacity-60 bg-gray-50' : ''}`}>
                <div className="flex justify-between items-center mb-4">
                  <span className={`py-1.5 px-3 rounded-md text-xs font-semibold ${
                    providers[key.provider]?.tier === 'free' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                  }`}>
                    {key.providerInfo?.name || key.provider}
                  </span>
                  <label className="relative inline-block w-10 h-[22px]">
                    <input
                      type="checkbox"
                      checked={key.isActive}
                      onChange={() => handleToggleActive(key.id, key.isActive)}
                      className="opacity-0 w-0 h-0"
                    />
                    <span className={`absolute cursor-pointer inset-0 rounded-[22px] transition-all before:absolute before:content-[''] before:h-4 before:w-4 before:left-[3px] before:bottom-[3px] before:bg-white before:rounded-full before:transition-all ${
                      key.isActive ? 'bg-blue-500 before:translate-x-[18px]' : 'bg-gray-300'
                    }`}></span>
                  </label>
                </div>

                <div className="mb-4">
                  <div className="mb-2">
                    {editingLabelId === key.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingLabelValue}
                          onChange={e => setEditingLabelValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleLabelEditSave(key.id);
                            if (e.key === 'Escape') handleLabelEditCancel();
                          }}
                          autoFocus
                          className="flex-1 py-2 px-3 text-sm border border-blue-500 rounded-md outline-none"
                        />
                        <button
                          onClick={() => handleLabelEditSave(key.id)}
                          className="py-1.5 px-2.5 text-xs font-semibold border-none rounded bg-blue-500 text-white cursor-pointer transition-all hover:bg-blue-600"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleLabelEditCancel}
                          className="py-1.5 px-2.5 text-xs font-semibold border-none rounded bg-gray-100 text-gray-600 cursor-pointer transition-all hover:bg-gray-200"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div
                        className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-gray-900 cursor-pointer transition-colors hover:text-blue-500 group"
                        onClick={() => handleLabelEditStart(key.id, key.label)}
                      >
                        {key.label}
                        <span className="text-xs opacity-0 transition-opacity group-hover:opacity-60">✎</span>
                      </div>
                    )}
                  </div>

                  <div className="font-mono text-xs text-gray-600 py-2 px-2.5 bg-slate-100 rounded mb-3">
                    {key.maskedKey}
                  </div>

                  <div className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-md">
                    <span className="text-xs font-medium text-gray-600">모델:</span>
                    <select
                      className="flex-1 py-1.5 px-2 border border-gray-200 rounded text-xs text-gray-900 bg-white cursor-pointer focus:outline-none focus:border-blue-500"
                      value={key.model || providers[key.provider]?.defaultModel || ''}
                      onChange={e => handleModelChange(key.id, e.target.value)}
                    >
                      {providers[key.provider]?.models?.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="text-xs text-gray-400">
                    사용: {key.usageCount.toLocaleString()}회
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-100 max-md:flex-col">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleTest(key.id)}
                    loading={testingId === key.id}
                  >
                    테스트
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleCheckBalance(key.id)}
                    loading={checkingBalanceId === key.id}
                  >
                    잔액
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(key)}
                  >
                    삭제
                  </Button>
                </div>

                {testResult && testResult.id === key.id && (
                  <div className={`mt-3 p-3 rounded-lg text-[13px] ${
                    testResult.success ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                  }`}>
                    {testResult.success ? '✓' : '✗'} {testResult.message}
                  </div>
                )}

                {balanceInfo[key.id] && (
                  <div className={`mt-3 p-3 rounded-lg text-[13px] ${
                    balanceInfo[key.id].supported ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
                  }`}>
                    {balanceInfo[key.id].supported ? (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-600">플랜:</span>
                          <span className="text-[13px] font-semibold text-gray-900">{balanceInfo[key.id].plan}</span>
                        </div>
                        {balanceInfo[key.id].usageThisMonth && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-600">이번달:</span>
                            <span className="text-[13px] font-semibold text-gray-900">${balanceInfo[key.id].usageThisMonth}</span>
                          </div>
                        )}
                        {balanceInfo[key.id].limit && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-600">한도:</span>
                            <span className="text-[13px] font-semibold text-gray-900">{balanceInfo[key.id].limit}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-xs text-amber-600">{balanceInfo[key.id].message}</div>
                        {balanceInfo[key.id].dashboardUrl && (
                          <a
                            href={balanceInfo[key.id].dashboardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block mt-2 text-xs font-medium text-blue-500 no-underline hover:underline"
                          >
                            대시보드에서 확인 →
                          </a>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* 통계 푸터 */}
      {stats && (
        <Card className="bg-white">
          <div className="flex gap-8 max-md:flex-col max-md:gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-[13px] text-gray-600">오늘 API 호출</span>
              <span className="text-2xl font-bold text-blue-500">{stats.todayUsage?.toLocaleString() || 0}회</span>
            </div>
            {stats.recentErrors?.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-[13px] text-gray-600">최근 오류</span>
                <span className="text-2xl font-bold text-red-500">{stats.recentErrors.length}건</span>
              </div>
            )}
          </div>
          {stats.recentErrors?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <h4 className="text-sm font-semibold text-red-500 m-0 mb-3">최근 오류 로그</h4>
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {stats.recentErrors.slice(0, 3).map((err, idx) => (
                  <li key={idx} className="flex gap-3 p-3 bg-red-50 rounded-lg text-[13px] max-md:flex-col max-md:gap-1">
                    <span className="font-semibold text-gray-900 min-w-[80px] max-md:min-w-0">{err.provider}</span>
                    <span className="text-red-600 flex-1">{err.errorMessage}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, key: null })}
        onConfirm={handleDeleteConfirm}
        title="API 키 삭제"
        message={`"${deleteModal.key?.label}" 키를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`}
        confirmText="삭제"
        cancelText="취소"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ApiKeyManager;
